import {
  Box,
  Stack,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Typography,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Select } from '@liminal/web-ui/forms/Select';
import { CopyableId } from '@liminal/web-ui/display/CopyableId';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { styled } from '@mui/material/styles';
import {
  TRANSACTIONS_PAGE_SIZE,
  useLedgerTransactionsPanel,
  useWalletTransactionsPanel,
} from '../hooks/useTransactionHistory';
import { ADMIN_CHAINS } from '@/hooks/useUsers';
import { formatOrderDate } from '@liminal/utils/date';
import { truncateMiddle } from '@liminal/utils/truncate';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import {
  txContextVariant,
  txStatusVariant,
  directionVariant,
  formatEnumLabel,
  walletTxStatusLabel,
  walletTxStatusVariant,
} from '@/components/ui/statusHelpers';
import { AccountIdLink } from '@/components/ui/AccountIdLink';
import { TransactionIdLink } from '@/components/ui/TransactionIdLink';
import { MonoCell } from '@/components/ui/MonoCell';
import { fontSizes, fontWeights } from '@liminal/brand/scale';
import { FONT_STACK_BODY } from '@liminal/brand/font-faces';
import { SearchInput } from '@liminal/web-ui/forms/SearchInput';
import { CursorPagination, Pagination } from '@liminal/web-ui/primitives/Pagination';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';

// Enum names as the ledger serialises them, so the values go on the wire
// unchanged. '' means "no filter" and is dropped before the request is built.
const CONTEXT_OPTIONS = [
  { label: 'All contexts', value: '' },
  { label: 'P2P', value: 'TRANSACTION_CONTEXT_P2P' },
  { label: 'Onramp', value: 'TRANSACTION_CONTEXT_ONRAMP' },
  { label: 'Offramp', value: 'TRANSACTION_CONTEXT_OFFRAMP' },
  { label: 'Fiat to fiat', value: 'TRANSACTION_CONTEXT_FIAT_TO_FIAT' },
  { label: 'Teleport', value: 'TRANSACTION_CONTEXT_TELEPORT' },
  { label: 'Savings deposit', value: 'TRANSACTION_CONTEXT_SAVINGS_DEPOSIT' },
  { label: 'Savings withdraw', value: 'TRANSACTION_CONTEXT_SAVINGS_WITHDRAW' },
  { label: 'Stock buy', value: 'TRANSACTION_CONTEXT_STOCK_BUY' },
  { label: 'Stock sell', value: 'TRANSACTION_CONTEXT_STOCK_SELL' },
  { label: 'Joint account deposit', value: 'TRANSACTION_CONTEXT_JOINT_ACCOUNT_DEPOSIT' },
  { label: 'Joint account withdraw', value: 'TRANSACTION_CONTEXT_JOINT_ACCOUNT_WITHDRAW' },
  { label: 'Refund', value: 'TRANSACTION_CONTEXT_REFUND' },
  { label: 'Pix payment', value: 'TRANSACTION_CONTEXT_PIX_PAYMENT' },
  { label: 'Pix received', value: 'TRANSACTION_CONTEXT_PIX_RECEIVE' },
  { label: 'Boleto payment', value: 'TRANSACTION_CONTEXT_PIX_BOLETO' },
  { label: 'Paid with LIL', value: 'TRANSACTION_CONTEXT_REWARD_REDEMPTION' },
];

const LEDGER_STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Pending', value: 'TRANSACTION_STATUS_PENDING' },
  { label: 'Confirmed', value: 'TRANSACTION_STATUS_CONFIRMED' },
  { label: 'Failed', value: 'TRANSACTION_STATUS_FAILED' },
];

// The wallets service stores status as free text, not an enum — these are the
// three words it writes (wallets/db/model/model.go).
const WALLET_STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Pending', value: 'pending' },
  { label: 'Success', value: 'success' },
  { label: 'Failed', value: 'failed' },
];

// The shared Select speaks string values; the chain id is parsed back on change.
const CHAIN_OPTIONS = ADMIN_CHAINS.map((chain) => ({ value: String(chain.id), label: chain.label }));

const LEDGER_COLUMN_COUNT = 9;
const WALLET_COLUMN_COUNT = 8;

// -- Styled components --

// A filter row inside a flush card: the §10 row, inset to the card's padding.
const FILTER_ROW_INSET = { px: 2, pb: 2 } as const;

const EntriesColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

const EntryRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  flexWrap: 'wrap',
});

const MetadataColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

const MetadataText = styled(Typography)({
  fontFamily: FONT_STACK_BODY,
  fontSize: fontSizes.xs,
});

const EntryAmountText = styled(Typography)({
  fontWeight: fontWeights.semibold,
});

// A failed fetch and an empty result render differently on purpose (the
// DataTableBody contract). This tab used to show "No wallet transactions
// found" for every user in the system, because it checked only isLoading and
// the on-chain request was in fact being rejected outright.
const LOAD_FAILED =
  'Failed to load transactions. The request to the API did not succeed — this is not the same as the user having none.';

// ---------------------------------------------------------------------------
// Ledger transactions
// ---------------------------------------------------------------------------

function LedgerTransactionsCard({ userId }: { userId: string }) {
  const panel = useLedgerTransactionsPanel(userId);

  return (
    <Card variant="elevated" padding="none">
        <Typography variant="h6" sx={{ px: 2, pt: 2, pb: 1 }}>
          Ledger Transactions
        </Typography>

        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
          sx={FILTER_ROW_INSET}
        >
          <Select
            variant="filter"
            aria-label="Context"
            size="sm"
            width={200}
            value={panel.context}
            onChange={panel.setContext}
            options={CONTEXT_OPTIONS}
          />
          <Select
            variant="filter"
            aria-label="Status"
            size="sm"
            width={160}
            value={panel.status}
            onChange={panel.setStatus}
            options={LEDGER_STATUS_OPTIONS}
          />
          <SearchInput
            value={panel.assetSymbol}
            onChange={panel.setAssetSymbol}
            placeholder="Asset (e.g. USDC)"
            width={200}
          />
        </Stack>

        <TableContainer>
          <Table size="small" sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <SortableTableCell column="id" sort={panel.sort}>
                  ID
                </SortableTableCell>
                <SortableTableCell column="context" sort={panel.sort}>
                  Context
                </SortableTableCell>
                <SortableTableCell column="status" sort={panel.sort}>
                  Status
                </SortableTableCell>
                <SortableTableCell column="note" sort={panel.sort}>
                  Note
                </SortableTableCell>
                <SortableTableCell column="createdAt" sort={panel.sort}>
                  Created At
                </SortableTableCell>
                <SortableTableCell column="updatedAt" sort={panel.sort}>
                  Updated At
                </SortableTableCell>
                <SortableTableCell column="confirmedAt" sort={panel.sort}>
                  Confirmed At
                </SortableTableCell>
                {/* Both stack several values in one cell, so there is nothing
                    single to order them by. */}
                <TableCell>Entries</TableCell>
                <TableCell>Metadata</TableCell>
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={LEDGER_COLUMN_COUNT}
              isLoading={panel.isLoading}
              isError={panel.isError}
              isEmpty={panel.transactions.length === 0}
              emptyMessage="No ledger transactions match these filters."
              errorMessage={LOAD_FAILED}
            >
              {panel.transactions.map((tx) => (
                  <TableRow key={tx.id} hover>
                    <MonoCell>
                      <TransactionIdLink transactionId={tx.id} />
                    </MonoCell>
                    <TableCell>
                      <StatusPill
                        label={formatEnumLabel(tx.context)}
                        tone={txContextVariant(tx.context)}
                        size="sm"
                      />
                    </TableCell>
                    <TableCell>
                      <StatusPill
                        label={formatEnumLabel(tx.status)}
                        tone={txStatusVariant(tx.status)}
                        size="sm"
                      />
                    </TableCell>
                    <TableCell>{tx.note ?? '—'}</TableCell>
                    <TableCell>{tx.createdAt ? formatOrderDate(tx.createdAt) : '—'}</TableCell>
                    <TableCell>{tx.updatedAt ? formatOrderDate(tx.updatedAt) : '—'}</TableCell>
                    <TableCell>{tx.confirmedAt ? formatOrderDate(tx.confirmedAt) : '—'}</TableCell>
                    <TableCell>
                      {tx.entries && tx.entries.length > 0 ? (
                        <EntriesColumn>
                          {tx.entries.map((entry, entryIndex) => (
                            <EntryRow key={entry.id ?? entryIndex}>
                              <StatusPill
                                label={formatEnumLabel(entry.direction)}
                                tone={directionVariant(entry.direction)}
                                size="sm"
                              />
                              <EntryAmountText variant="caption">
                                {entry.amount} {entry.assetSymbol}
                              </EntryAmountText>
                              <Typography variant="caption" color="text.disabled">
                                {'('}
                                <AccountIdLink accountId={entry.accountId} />
                                {')'}
                              </Typography>
                            </EntryRow>
                          ))}
                        </EntriesColumn>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {tx.metadata && Object.keys(tx.metadata).length > 0 ? (
                        <MetadataColumn>
                          {Object.entries(tx.metadata).map(([key, value]) => (
                            <MetadataText key={key} variant="caption">
                              {key}: {value}
                            </MetadataText>
                          ))}
                        </MetadataColumn>
                      ) : '—'}
                    </TableCell>
                  </TableRow>
              ))}
            </DataTableBody>
          </Table>
        </TableContainer>

        <Box sx={{ px: 2, pb: 2 }}>
          <CursorPagination
            firstRowNumber={panel.firstRowNumber}
            count={panel.transactions.length}
            hasPreviousPage={panel.hasPreviousPage}
            hasNextPage={panel.hasNextPage}
            onPreviousPage={panel.goToPreviousPage}
            onNextPage={panel.goToNextPage}
            disabled={panel.isLoading}
          />
        </Box>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Wallet (on-chain) transactions
// ---------------------------------------------------------------------------

function WalletTransactionsCard({ userId }: { userId: string }) {
  const panel = useWalletTransactionsPanel(userId);

  return (
    <Card variant="elevated" padding="none">
        <Typography variant="h6" sx={{ px: 2, pt: 2, pb: 1 }}>
          Wallet Transactions (On-Chain)
        </Typography>

        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
          sx={FILTER_ROW_INSET}
        >
          {/* The wallets service filters on a single chain per query and
              rejects a request without one, so this is a required choice
              rather than an "all chains" filter. */}
          <Select
            variant="filter"
            aria-label="Chain"
            size="sm"
            width={200}
            value={String(panel.chainId)}
            onChange={(chainId) => panel.setChainId(Number(chainId))}
            options={CHAIN_OPTIONS}
          />
          <Select
            variant="filter"
            aria-label="Status"
            size="sm"
            width={160}
            value={panel.status}
            onChange={panel.setStatus}
            options={WALLET_STATUS_OPTIONS}
          />
        </Stack>

        <TableContainer>
          <Table size="small" sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <SortableTableCell column="transactionHash" sort={panel.sort}>
                  Tx Hash
                </SortableTableCell>
                <SortableTableCell column="userOperationHash" sort={panel.sort}>
                  User Op Hash
                </SortableTableCell>
                <SortableTableCell column="tokenSymbol" sort={panel.sort}>
                  Token
                </SortableTableCell>
                <SortableTableCell column="amountHuman" sort={panel.sort}>
                  Amount
                </SortableTableCell>
                <SortableTableCell column="recipientAddress" sort={panel.sort}>
                  Recipient
                </SortableTableCell>
                <SortableTableCell column="status" sort={panel.sort}>
                  Status
                </SortableTableCell>
                <SortableTableCell column="createdAt" sort={panel.sort}>
                  Created At
                </SortableTableCell>
                <SortableTableCell column="errorMessage" sort={panel.sort}>
                  Error
                </SortableTableCell>
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={WALLET_COLUMN_COUNT}
              isLoading={panel.isLoading}
              isError={panel.isError}
              isEmpty={panel.transactions.length === 0}
              emptyMessage="No on-chain transactions on this chain match these filters."
              errorMessage={LOAD_FAILED}
            >
              {panel.transactions.map((tx) => (
                  <TableRow key={tx.transactionId} hover>
                    <TableCell>
                      {tx.transactionHash ? (
                        <CopyableId value={tx.transactionHash} truncate label="Copy transaction hash" />
                      ) : '—'}
                    </TableCell>
                    <MonoCell>
                      {tx.userOperationHash ? truncateMiddle(tx.userOperationHash) : '—'}
                    </MonoCell>
                    <TableCell>{tx.tokenSymbol ?? '—'}</TableCell>
                    <TableCell>{tx.amountHuman ?? '—'}</TableCell>
                    <MonoCell>
                      {tx.recipientAddress ? truncateMiddle(tx.recipientAddress) : '—'}
                    </MonoCell>
                    <TableCell>
                      <StatusPill
                        label={walletTxStatusLabel(tx.status)}
                        tone={walletTxStatusVariant(tx.status)}
                        size="sm"
                      />
                    </TableCell>
                    <TableCell>{tx.createdAt ? formatOrderDate(tx.createdAt) : '—'}</TableCell>
                    <TableCell>{tx.errorMessage || '—'}</TableCell>
                  </TableRow>
              ))}
            </DataTableBody>
          </Table>
        </TableContainer>

        <Box sx={{ px: 2, pb: 2 }}>
          <Pagination
            offset={panel.offset}
            pageSize={TRANSACTIONS_PAGE_SIZE}
            count={panel.transactions.length}
            onOffsetChange={panel.setOffset}
            disabled={panel.isLoading}
          />
        </Box>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Section root
// ---------------------------------------------------------------------------

/**
 * The two tables filter and page independently. They used to share one loading
 * gate, which meant a slow or failing on-chain lookup held the ledger table
 * back for no reason.
 */
export function TransactionHistorySection({ userId }: { userId: string }) {
  return (
    <Stack spacing={3}>
      <LedgerTransactionsCard userId={userId} />
      <WalletTransactionsCard userId={userId} />
    </Stack>
  );
}
